import type { ImportReport, ResolvedRecipe } from '@/api/types'

/**
 * The import in progress, kept in sessionStorage so that a refresh, or the round
 * trip through the recipe form, does not lose it. The original file is kept as
 * well as the report: the report goes stale as soon as an ingredient is created
 * during review, so the import page resolves the file again on every visit.
 */
export interface ImportSession {
  file: unknown
  report: ImportReport
  /** Recipe index in the file → id of the recipe it was saved as. */
  saved: Record<number, number>
}

const KEY = 'mp_import'

export function loadImportSession(): ImportSession | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as ImportSession) : null
  } catch {
    return null
  }
}

export function saveImportSession(session: ImportSession): void {
  sessionStorage.setItem(KEY, JSON.stringify(session))
}

export function clearImportSession(): void {
  sessionStorage.removeItem(KEY)
}

/** The recipe at `index`, or null when there is no such import in progress. */
export function importedRecipe(index: number): ResolvedRecipe | null {
  return loadImportSession()?.report.recipes[index] ?? null
}

/** Record a save. Returns whether any recipe in the import is still open. */
export function markImportSaved(index: number, recipeId: number): boolean {
  const session = loadImportSession()
  if (!session) return false
  session.saved[index] = recipeId
  saveImportSession(session)
  return session.report.recipes.some((_, i) => session.saved[i] === undefined)
}

/** How a line reads at a glance: ready, to create, or needing a decision. */
export function lineCounts(recipe: ResolvedRecipe) {
  let ready = 0
  let create = 0
  let attention = 0
  for (const line of recipe.lines) {
    if (line.issues.length > 0 || line.status === 'unknown' || line.status === 'text_only') {
      attention += 1
    } else if (line.status === 'new') {
      create += 1
    } else {
      ready += 1
    }
  }
  return { ready, create, attention }
}

/**
 * Pull the JSON document out of what was pasted: LLMs like to wrap it in a
 * ```json fence or add a sentence around it.
 */
export function extractJson(text: string): string {
  const fenced = /```(?:json)?\s*\n([\s\S]*?)```/i.exec(text)
  if (fenced?.[1]) return fenced[1].trim()
  const start = text.indexOf('{')
  const end = text.lastIndexOf('}')
  return start >= 0 && end > start ? text.slice(start, end + 1) : text.trim()
}
